




import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import SceneOptions from "@/models/three/SceneOptions";

import {
  Scene,
  PerspectiveCamera,
  CanvasTexture,
  MeshPhysicalMaterial,
  WebGLRenderer,
  MeshBasicMaterial,
  LinearToneMapping,
  Color,
  PMREMGenerator,
  Fog,
  PCFSoftShadowMap,
  Mesh,
  DoubleSide,
  Vector2,
  Box3,
  Vector3,
  Raycaster,
  TextureLoader,
  Object3D,
  MeshStandardMaterial,
  SphereGeometry,
  LinearFilter,
} from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { Subscription } from "rxjs";
import { Component as ModelComponent } from "@/models";
import {
  ClipArea,
  PrintArea,
  PrintAreaCustomization,
  WarehouseItem,
} from "@/models";
import { v1 } from "uuid";
import { METER } from "@/helpers/interaction";
import MeshModel from "@/models/three/MeshModel";
import { customizationStore } from "@/store/typed";
import { ThemeColors } from "@/constants";
import { RoomEnvironment } from "@/helpers/Three";

@Component({ components: {} })
export default class Canvas extends Vue {
  @Prop({
    default: () => {
      return {
        zoom: 5,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
      };
    },
  })
  readonly objectConfig!: any;
  @Prop() readonly path!: string;
  TWEEN = require("@tweenjs/tween.js");

  private subscriptions: Subscription[] = [];
  private raycaster: Raycaster = new Raycaster();
  private camera!: PerspectiveCamera;
  private model?: any = null;
  private renderer: WebGLRenderer = new WebGLRenderer({
    antialias: true,
  });
  private size: Vector2;
  private uuid: string = v1();
  private mouse = new Vector2();
  private intersected: any = null;
  private controls?: OrbitControls = undefined;
  private scene: Scene = new Scene();
  private images: any = {};
  autorotate = true;

  private sceneConfig: SceneOptions = {
    meshColor: ThemeColors.Black,
    meshSelectedColor: 0x3497fd,
    backgroundColor: ThemeColors.Dark,
    floorColor: 0xfbfbfb,
  };

  mounted() {
    this.setupScene();
  }

  @Watch("path")
  onPathChanged(val: string, newval: string) {
    if (this.scene) {
      this.scene.remove(this.model);
      this.scene = new Scene();
      this.setupScene();
    }
  }

  toggleAutorotate(autorotate: boolean) {
    this.autorotate = autorotate;
  }

  get sceneElement() {
    return this.$refs.scene as Element;
  }

  get modelPath() {
    return this.path;
  }

  meshes(): Mesh[] {
    const objects: Mesh[] = [];
    (this.model as any).traverse((o: any) => {
      if (o.isMesh) {
        objects.push(o as Mesh);
      }
    });

    return objects;
  }

  loadImage(path: string) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = path;
      img.onload = () => {
        resolve(img);
      };
      img.onerror = (e) => {
        reject(e);
      };
    });
  }

  downloadImage(data: any, filename = "untitled.jpeg") {
    let a = document.createElement("a");
    a.href = data;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
  }

  get clipArea(): ClipArea {
    return this.printArea?.clipArea;
  }

  get printArea(): PrintArea {
    return customizationStore.printArea;
  }

  get warehouseItem(): WarehouseItem {
    return customizationStore.warehouseItem;
  }

  async loadImages() {
    const pacs = customizationStore.printAreaCustomizations.filter(
      (o: any) =>
        o.warehouseItemCustomizationId ==
        customizationStore.currentWarehouseItemCustomization.id
    );

    for await (const pac of pacs) {
      if (pac.imageOutput) {
        this.images[pac.id] = await this.loadImage(pac.imageOutput);
      }
    }
  }

  async applyTexture(printAreaCustomization: PrintAreaCustomization) {
    const componentId = printAreaCustomization?.printArea?.componentId;
    const component = customizationStore.components.find(
      (c: any) => c.id == componentId
    )?.code;

    const printArea = printAreaCustomization.printArea;

    let mesh: any | null = null;
    if (!this.model) {
      return;
    }
    (this.model as any).traverse((o: any) => {
      if (o.name === component) {
        mesh = o;
      }
    });

    if (mesh && printAreaCustomization.imageOutput) {
      const offsets = [printArea.textureOffsetX, printArea.textureOffsetY];
      const scales = [printArea.textureScaleX, printArea.textureScaleY];

      const texture: any = await this.createTexture(
        printAreaCustomization,
        printAreaCustomization.imageOutput,
        scales,
        offsets
      );
      mesh.material.color = null;
      mesh.material.map = texture;
      mesh.material.needsUpdate = true;
    }
  }

  async createTexture(
    printAreaCustomization: PrintAreaCustomization,
    textureUrl: string,
    scales: number[],
    offsets: number[]
  ) {
    const newCanvas = document.createElement("canvas") as HTMLCanvasElement;
    const clipArea = printAreaCustomization.printArea.clipArea;
    const scaleFactor = printAreaCustomization.printArea.scaleFactor || 1;
    const newWidth = clipArea.width * scaleFactor;
    const newHeight = clipArea.height * scaleFactor;
    newCanvas.width = newWidth;
    newCanvas.height = newHeight;
    const ctx = newCanvas.getContext("2d");
    ctx.fillStyle = printAreaCustomization.printArea.defaultBackgroundColor;
    ctx.fillRect(0, 0, newWidth, newHeight);
    ctx.drawImage(
      this.images[printAreaCustomization.id],
      newWidth * offsets[0],
      newHeight * offsets[1],
      newWidth * scales[0],
      newHeight * scales[1]
    );

    let texture = new CanvasTexture(newCanvas);

    try {
      texture = await new Promise((resolve, reject) => {
  
        const url = newCanvas.toDataURL("image/png", 1.0);

        new TextureLoader().load(
          url,
          (data) => {
            resolve(data);
          },
          (progress) => {
            console.log(progress)
          },
          (error) => {
            reject(error);
          }
        );
      });
      console.log("Texture loaded ✅");
      texture.minFilter = LinearFilter;
      texture.flipY = false;
      texture.needsUpdate = true;
      return texture;
    } catch (e) {
      console.warn("Couldn't load texture ⛔️");
    }
  }

  resizeModel() {
    let bbox = new Box3().setFromObject(this.model);
    let cent = bbox.getCenter(new Vector3());
    let size = bbox.getSize(new Vector3());
    let maxAxis = Math.max(size.x, size.y, size.z);
    this.model.scale.multiplyScalar(1.0 / maxAxis);
    bbox.setFromObject(this.model);
    bbox.getCenter(cent);
    bbox.getSize(size);
    this.model.position.copy(cent).multiplyScalar(-1);
  }

  initMaterial() {
    (this.model as any).traverse((o: any) => {
      if (o.isMesh) {
        let color = new Color(this.sceneConfig.meshColor);
        const component = customizationStore.componentByCode(o.name);
        if (component.configuration?.color) {
          color = new Color(`${component.configuration.color}`);
        } else {
          color = new Color(`#000000`);
        }

        o.material = this.setMaterialType(component);
        o.material.color = color;
        o.userData["color"] = color;
      }
    });
  }

  standardMaterial(component: ModelComponent) {
    const mat = new MeshStandardMaterial({
      // side: DoubleSide,
      metalness: component.configuration?.metalness ?? 0,
      roughness: component.configuration?.roughness ?? 0.4,
      transparent: !!component.configuration.opacity,
      opacity: component.configuration?.opacity ?? 1,
    });

    if (!component.printable) {
      mat.side = DoubleSide;
    }

    return mat;
  }

  physicalMaterial(component: ModelComponent) {
    const mat = new MeshPhysicalMaterial({
      // side: DoubleSide,
      roughness: component.configuration?.roughness ?? 0.4,
      metalness: component.configuration.metalness ?? 0,
      transmission: 1,
      opacity: component.configuration.opacity ?? 1,
      clearcoatMap: null,
      clearcoatRoughnessMap: null,
      transparent: !!component.configuration.opacity,
    });

    if (!component.printable) {
      mat.side = DoubleSide;
    }

    return mat;
  }

  setMaterialType(component: ModelComponent) {
    switch (component.configuration.materialType) {
      case "MeshStandardMaterial":
        return this.standardMaterial(component);
      case "MeshPhysicalMaterial":
        return this.physicalMaterial(component);
      default:
        return this.standardMaterial(component);
    }
  }

  degToRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  loadGLTF(callback: any) {
    const loader = new GLTFLoader();
    loader.load(
      this.modelPath,
      (gltf) => {
        this.model = gltf.scene;
        this.model.rotateX(this.degToRad(this.objectConfig.rotationX));
        this.model.rotateY(this.degToRad(this.objectConfig.rotationY));
        this.model.rotateZ(this.degToRad(this.objectConfig.rotationZ));
        callback();
      },
      undefined,
      (error) => {
        console.error(error);
      }
    );
  }

  loadOBJ(callback: any) {
    const loader = new OBJLoader();
    loader.load(
      this.modelPath,
      (obj) => {
        this.model = obj;
        callback();
      },
      undefined,
      (error) => {
        console.error(error);
      }
    );
  }

  async onObjectLoaded() {
    this.resizeModel();
    this.setupLights();
    this.initMaterial();
    this.model.castShadow = true;
    this.scene.add(this.model);
    this.setUpAnimationFunction();

    //     this.model.traverse((object) => {
    //       const tween1 = new this.TWEEN.Tween({ x: object.position.x, y: object.position.y })
    //         .to({ x: object.position.x+Math.random()*0.2, y: object.position.y+Math.random()*0.3 }, 2000)
    //         .onUpdate((coords) => {
    //       // if (object.id == 16 || object.id == 17) {
    //       object.position.x = coords.x;
    //         object.position.y = coords.y;
    //       // }
    //   })
    //   .easing(this.TWEEN.Easing.Exponential.InOut)
    //   .repeat(Infinity)
    //   .delay(500);
    // tween1.start();
    //     });

    const models: MeshModel[] = [];
    for (const mesh of this.meshes()) {
      models.push(new MeshModel(mesh));
    }
    await this.applyTextures();
    console.log("All textures applied ✅");
    this.$emit("loaded");
  }

  async applyTextures(): Promise<void> {
    const pacs = customizationStore.printAreaCustomizations.filter(
      (o: any) =>
        o.warehouseItemCustomizationId ==
        customizationStore.currentWarehouseItemCustomization.id
    );
    for await (const pac of pacs) {
      await this.applyTexture(pac);
    }
  }

  loadObject(callback: any) {
    const splitted = this.modelPath.split(".");
    switch (splitted[splitted.length - 1]) {
      case "obj":
        this.loadOBJ(callback);
        break;

      case "glb":
      case "gltf":
        this.loadGLTF(callback);
        break;
    }
    this.camera.updateProjectionMatrix();
  }

  isChildOf(child: Object3D, parent: Object3D): boolean {
    parent.traverse((o) => {
      if (child.uuid === o.uuid) {
        return true;
      }
    });
    return false;
  }

  async setupScene() {
    const config = this.sceneConfig;
    this.scene.background = new Color(config.backgroundColor);
    this.scene.fog = new Fog(config.backgroundColor as number, 20, 100);
    this.setupCamera();
    this.setupOrbitControls();
    this.size = new Vector2(
      this.sceneElement.clientWidth,
      this.sceneElement.clientHeight || this.sceneElement.clientWidth * (9 / 16)
    );
    this.renderer.setClearColor(0xeeeeee);
    this.renderer.setSize(this.size.x, this.size.y);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.camera.aspect = this.size.x / this.size.y;
    this.camera.updateProjectionMatrix();
    this.sceneElement.appendChild(this.renderer.domElement);
    if (this.modelPath) {
      await this.loadImages();
      await this.loadObject(this.onObjectLoaded);
    } else {
      this.loadPlaceholder();
    }
  }

  loadPlaceholder() {
    let geometry = new SphereGeometry(5, 32, 32);
    let material = new MeshBasicMaterial({ color: this.sceneConfig.meshColor });
    let sphere = new Mesh(geometry, material);
    this.model = sphere;
    this.scene.add(this.model);
    this.onObjectLoaded();
  }

  setUpAnimationFunction() {
    const animate = (t) => {
      this.TWEEN.update(t);
      requestAnimationFrame(animate);
      this.renderer.render(this.scene, this.camera);
      if (this.autorotate) {
        this.controls.update();
      }
    };
    animate(0);
  }

  setupOrbitControls(): void {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.minPolarAngle = -Math.PI * 2;
    this.controls.maxPolarAngle = Math.PI * 2;
    this.controls.enableDamping = true;
    this.controls.maxZoom = 1;
    this.controls.enablePan = false;
    this.controls.dampingFactor = 0.1;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 4;
  }

  setupLights(): void {
    const box = new Box3().setFromObject(this.model);
    const wia = this.warehouseItem.warehouseItemArtifact;
    const environment = new RoomEnvironment(box, {
      count: wia.lightsCount,
      intensity: wia.lightsIntensity,
      radiusMultiplier: wia.lightsRadiusMultiplier,
    }).build();
    const pmremGenerator = new PMREMGenerator(this.renderer);
    this.scene.environment = pmremGenerator.fromScene(environment).texture;
    this.renderer.toneMapping = LinearToneMapping;
    this.renderer.toneMappingExposure = 0.8;
  }

  destroyed() {
    this.renderer.forceContextLoss();
  }

  setupCamera() {
    this.camera = new PerspectiveCamera(
      25,
      innerWidth / innerHeight,
      0.1,
      3000 * METER
    );
    this.camera.position.z = this.objectConfig.zoom;
  }
}
